import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { num, eur, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Award, Gift, Ticket, Lock } from "lucide-react";

const REASON_MSG = {
  punti_insufficienti: "Punti insufficienti",
  esaurito: "Premio esaurito",
  scaduto: "Premio scaduto",
  limite_raggiunto: "Limite raggiunto",
  non_attivo: "Non disponibile",
};
const V_STATUS = {
  unused: { label: "Disponibile", token: "--hi-status-success" },
  used: { label: "Utilizzato", token: "--muted-foreground" },
  cancelled: { label: "Annullato", token: "--destructive" },
};

export default function PortalRewards() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/portal/rewards-catalog").then((r) => setD(r.data)).catch(() => setD({ balance: 0, catalog: [], vouchers: [] })), []);
  useEffect(() => { load(); }, [load]);

  const redeem = async (reward) => {
    setBusy(reward.id);
    try {
      const { data } = await api.post(`/portal/rewards/${reward.id}/redeem`);
      toast.success(`Premio riscattato! Voucher ${data.voucher.code}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile riscattare"); }
    finally { setBusy(null); }
  };

  if (!d) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>;

  const catalog = d.catalog || [];
  const vouchers = d.vouchers || [];
  const activeVouchers = vouchers.filter((v) => v.status === "unused");

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-semibold">Identity Rewards</h1>

      <Card className="p-6 hi-plaque text-[hsl(36_44%_96%)] text-center">
        <Award className="h-8 w-8 mx-auto text-[hsl(42_60%_52%)]" />
        <div className="text-xs uppercase tracking-wider opacity-70 mt-2">Saldo punti</div>
        <div className="font-heading text-5xl font-semibold" data-testid="portal-points-balance">{num(d.balance)}</div>
      </Card>

      {/* Catalogo premi */}
      <div>
        <div className="flex items-center gap-2 mb-3"><Gift className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h2 className="font-heading text-lg font-semibold">Catalogo premi</h2></div>
        {catalog.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">Nessun premio disponibile al momento.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {catalog.map((r) => (
              <Card key={r.id} className="p-4 flex flex-col" data-testid={`portal-reward-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.name}</div>
                    {r.item_name && <div className="text-xs text-muted-foreground truncate">Su: {r.item_name}</div>}
                  </div>
                  <Badge variant="secondary" className="shrink-0">{num(r.points_required)} pt</Badge>
                </div>
                {r.description && <p className="text-sm text-muted-foreground mt-1.5 flex-1">{r.description}</p>}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm font-medium text-[hsl(var(--hi-gold))]">{r.value_eur ? `Valore ${eur(r.value_eur)}` : ""}</span>
                  {r.can_redeem ? (
                    <Button size="sm" onClick={() => redeem(r)} disabled={busy === r.id} data-testid={`portal-redeem-${r.id}`}>
                      {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gift className="h-4 w-4 mr-1" /> Riscatta</>}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled data-testid={`portal-locked-${r.id}`}><Lock className="h-3.5 w-3.5 mr-1" />{REASON_MSG[r.reason] || "Non disponibile"}</Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* I miei voucher */}
      <div>
        <div className="flex items-center gap-2 mb-3"><Ticket className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h2 className="font-heading text-lg font-semibold">I miei voucher{activeVouchers.length ? ` (${activeVouchers.length} disponibili)` : ""}</h2></div>
        {vouchers.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">Nessun voucher. Riscatta un premio dal catalogo.</Card>
        ) : (
          <div className="space-y-2">
            {vouchers.map((v) => {
              const st = V_STATUS[v.status] || V_STATUS.unused;
              return (
                <Card key={v.id} className="p-3 flex items-center justify-between gap-3" data-testid={`portal-voucher-${v.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-sm">{v.code}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: `hsl(var(${st.token}))` }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(${st.token}))` }} />{st.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{v.reward_name}{v.expires_at ? ` · scade ${fmtDate(v.expires_at)}` : ""}</div>
                  </div>
                  <span className="font-semibold tabular text-[hsl(var(--hi-gold))] shrink-0">{eur(v.value_eur)}</span>
                </Card>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-2">Mostra il codice voucher in salone per usufruire del premio.</p>
      </div>
    </div>
  );
}
